import {
  BackendError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
  UnprocessableEntityError,
} from "@exceptions";

export interface MutationDefaultOptions {
  errorKey: string;
}

export interface PaginationInfo {
  total: number;
  count: number;
  per_page: number;
  current_page: number;
  total_pages: number;
  links: [];
}

export type FilterableFields = {
  [key: string]: "text" | "boolean";
};

export interface BackendResponseFormat<T> {
  data?: T;
  message?: string;
  errors?: { [key: string]: string[] };
  meta?: {
    pagination?: PaginationInfo;
    sortable_fields?: string[];
    filterable_fields?: FilterableFields;
    error_code?: number;
  };
}

interface FetchOptions {
  url: string;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: HeadersInit;
  body?: unknown;
  language?: string;
  multipart?: boolean;
  accept?: string;
}

/**
 * Converts form data to FormData if it contains files, otherwise returns the original data
 * @param data - The form data to potentially convert
 * @returns Object with the processed body and whether it should use multipart
 */
export function prepareRequestBody<T extends object>(
  data: T,
): {
  body: FormData | T;
  multipart: boolean;
} {
  // Check if data contains files for multipart submission
  const hasFiles = Object.values(data as Record<string, unknown>).some(
    (value) => value instanceof FileList || value instanceof File,
  );

  if (!hasFiles) {
    return { body: data, multipart: false };
  }

  // Convert to FormData for file uploads
  const formData = new FormData();

  /**
   * Recursively append nested objects and arrays to FormData
   * @param obj - The object to append
   * @param prefix - The field name prefix for nested keys
   */
  const appendToFormData = (
    obj: Record<string, unknown>,
    prefix = "",
  ): void => {
    Object.entries(obj).forEach(([key, value]) => {
      const fieldName = prefix ? `${prefix}[${key}]` : key;

      if (value instanceof FileList && value.length > 0) {
        // Handle FileList (from file input)
        for (let i = 0; i < value.length; i++) {
          formData.append(fieldName, value[i]);
        }
      } else if (value instanceof File) {
        // Handle single File
        if (value.size > 50 * 1024 * 1024) {
          throw new Error(`File ${value.name} exceeds maximum size limit`);
        }
        formData.append(fieldName, value);
      } else if (Array.isArray(value)) {
        // Handle arrays by iterating with index
        value.forEach((item, index) => {
          if (typeof item === "object" && item !== null) {
            appendToFormData(
              item as Record<string, unknown>,
              `${fieldName}[${index}]`,
            );
          } else if (item !== null && item !== undefined) {
            formData.append(`${fieldName}[${index}]`, String(item));
          }
        });
      } else if (typeof value === "object" && value !== null) {
        // Handle nested objects
        appendToFormData(value as Record<string, unknown>, fieldName);
      } else if (value !== null && value !== undefined) {
        // Handle primitive values
        formData.append(fieldName, String(value));
      } else if (value === null) {
        // Handle null values explicitly for file deletion
        formData.append(fieldName, "");
      }
    });
  };

  appendToFormData(data as Record<string, unknown>);

  return { body: formData, multipart: true };
}

function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
}

export async function backendFetch({
  url,
  method = "GET",
  headers = {},
  body,
  language = "en-US",
  multipart = false,
}: FetchOptions) {
  const xsrfToken = getCookie("XSRF-TOKEN");
  const defaultHeaders: Record<string, string> = {
    Accept: "application/json",
    "Accept-Language": language,
  };

  if (!multipart) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  if (xsrfToken) {
    defaultHeaders["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);
  }

  let requestBody: BodyInit | undefined;

  if (body) {
    if (multipart) {
      requestBody = body as FormData;
    } else {
      requestBody = JSON.stringify(body);
    }
  } else {
    requestBody = undefined;
  }

  const response = await fetch(url, {
    method,
    headers: {
      ...defaultHeaders,
      ...headers,
    },
    body: requestBody,
    credentials: "include",
  });

  const contentType = response.headers.get("content-type");

  let responseBody: BackendResponseFormat<unknown> | string | Blob;
  if (contentType?.includes("application/json")) {
    responseBody = await response.json();
  } else if (contentType?.includes("application/pdf")) {
    responseBody = await response.blob();
  } else {
    responseBody = await response.text();
  }

  if (!response.ok) {
    const statusCode = response.status;

    if (responseBody instanceof Blob) {
      responseBody = await responseBody.text();
    }

    switch (statusCode) {
      case 401:
        throw new UnauthorizedError(responseBody);
      case 403:
        throw new ForbiddenError(responseBody);
      case 404:
        throw new NotFoundError(responseBody);
      case 422:
        throw new UnprocessableEntityError(responseBody);
      default:
        throw new BackendError(statusCode, responseBody);
    }
  }

  return responseBody;
}
